// OWNED BY the meetings tab agent (created under detail/tabs/meetings/).
//
// Single data hook shared by the tab body AND the tab-strip badge (contact-
// scoped query approach mirrored from the tasks tab, without importing it):
//   - live source: CalendarContext's onSnapshot on the user's own calendar
//     (started idempotently via initiateLoadEvents — the same listener the
//     calendar page / dashboard / tasks tab use, so no extra churn);
//   - cold-start fallback: the session-cache snapshot warmed by
//     preloadMeetingsTab on contacts-row hover (renders instantly; the live
//     listener takes over on its first emission);
//   - live emissions are mirrored back into the session cache so reopening
//     the page (and the badge) stay warm.

import { useEffect, useMemo, useState } from "react";
import useAuth from "hooks/useAuth";
import useCalendar from "hooks/useCalendar";
import { getCached, setCached, subscribeCached } from "../../data/sessionCache";
import {
  isEventOfContact,
  isMeetingEvent,
  isMeetingToday,
  ownCalendarEventsCacheKey,
  splitContactMeetings
} from "./meetingsData";
import type { CalendarEvent, RealEstateContact } from "../../../../../../@types/sharedSchema";

export type ContactMeetingsState = {
  /** True only on a true cold start (no cache, live listener not emitted yet). */
  loading: boolean;
  /** True once CalendarContext's live events are available. */
  live: boolean;
  /** Upcoming (start day >= today), soonest first. */
  upcoming: CalendarEvent[];
  /** Past, most recent first. */
  past: CalendarEvent[];
  /** How many of the upcoming meetings are today. */
  todayCount: number;
};

export function useContactMeetings(contact: RealEstateContact): ContactMeetingsState {
  const { user } = useAuth();
  const { events, initiateLoadEvents } = useCalendar();

  const userEmail: string | undefined = user?.email ?? undefined;

  // Start the own-calendar live listener. CalendarContext dedups internally
  // (a boolean flag), so calling on every mount is safe and idempotent.
  useEffect(() => {
    initiateLoadEvents?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-render when the preloaded snapshot lands in the session cache.
  const [, forceRender] = useState(0);
  useEffect(() => {
    if (!userEmail) return;
    return subscribeCached(ownCalendarEventsCacheKey(userEmail), () => forceRender((n) => n + 1));
  }, [userEmail]);

  // Mirror live emissions into the session cache (keeps future opens instant).
  useEffect(() => {
    if (events && userEmail) setCached(ownCalendarEventsCacheKey(userEmail), events);
  }, [events, userEmail]);

  const cachedEvents = userEmail
    ? getCached<CalendarEvent[]>(ownCalendarEventsCacheKey(userEmail))
    : undefined;

  const sourceEvents: CalendarEvent[] | undefined = events ?? cachedEvents;
  const live = !!events;
  const loading = sourceEvents === undefined;

  const meetings = useMemo(
    () =>
      (sourceEvents ?? []).filter(
        (event) => isMeetingEvent(event) && isEventOfContact(event, contact)
      ),
    // The contact prop identity churns on live updates — key on the phone fields instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceEvents, contact.id, contact.contactId]
  );

  const { upcoming, past } = useMemo(() => splitContactMeetings(meetings), [meetings]);

  const todayCount = useMemo(
    () => upcoming.filter((meeting) => isMeetingToday(meeting)).length,
    [upcoming]
  );

  return { loading, live, upcoming, past, todayCount };
}
